import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import LanguageCurrencySelector from "../components/LanguageCurrencySelector";
import { useCurrency, formatPrice } from "../context/CurrencyContext";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { Check, Sparkles, Calendar, Zap, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

// Base USD pricing (matches backend PLANS)
const SETUP_USD = 14995;
const MONTHLY_USD = 4995;
const ANNUAL_USD = Math.round(12 * 4995 * 0.7); // 41958

export default function Pricing() {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const { user } = useAuth();
  const { sub, status, refresh } = useSubscription();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  useEffect(() => { refresh(); }, [refresh]);

  const startTrial = async () => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/pricing";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setBusy("trial");
    try {
      await api.post("/billing/start-trial");
      await refresh();
      toast.success(t("pricing.trialActive"));
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start trial");
    } finally { setBusy(null); }
  };

  const subscribe = async (planId) => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/pricing";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        currency,
        origin_url: window.location.origin
      });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout");
      setBusy(null);
    }
  };

  const includesSetup = !sub?.setup_paid;
  const monthlyTotal = (includesSetup ? SETUP_USD : 0) + MONTHLY_USD;
  const annualTotal = (includesSetup ? SETUP_USD : 0) + ANNUAL_USD;

  const Card = ({ id, kicker, title, desc, mainPrice, period, total, totalLabel, cta, ctaIcon: CtaIcon, featured, onClick, children, badge, currentLabel }) => (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: id * 0.1 }}
      className={`relative tray p-7 flex flex-col ${featured ? "ring-2 ring-amber-500/50" : ""}`}
      data-testid={`plan-card-${id}`}
    >
      {badge && (
        <div className="absolute -top-3 left-6 px-3 py-1 bg-amber-500 text-black text-[10px] font-mono-data tracking-widest uppercase rounded">
          {badge}
        </div>
      )}
      <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-amber-500 mb-2">{kicker}</div>
      <h3 className="font-display text-3xl mb-1">{title}</h3>
      <p className="text-text-secondary text-sm mb-6 min-h-[40px]">{desc}</p>

      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="font-display text-5xl text-amber-500">{mainPrice}</span>
          {period && <span className="font-mono-data text-sm text-text-secondary">{period}</span>}
        </div>
        {total && (
          <div className="mt-3 pt-3 border-t border-white/5">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-text-secondary">{totalLabel}</div>
            <div className="font-mono-data text-base mt-0.5">{total}</div>
          </div>
        )}
      </div>

      {children}

      <button
        onClick={onClick}
        disabled={busy === id || currentLabel}
        data-testid={`plan-cta-${id}`}
        className={`btn-tactile ${featured ? "btn-amber" : ""} w-full mt-auto inline-flex items-center justify-center gap-2 disabled:opacity-50`}
      >
        {busy === id ? <Loader2 className="w-4 h-4 animate-spin" /> : CtaIcon && <CtaIcon className="w-4 h-4" />}
        {currentLabel || cta}
      </button>
    </motion.div>
  );

  const features = [
    t("pricing.features.f1"), t("pricing.features.f2"), t("pricing.features.f3"),
    t("pricing.features.f4"), t("pricing.features.f5"), t("pricing.features.f6"),
    t("pricing.features.f7"), t("pricing.features.f8"),
  ];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="pricing-page">
        <div className="flex justify-between items-start flex-wrap gap-4 mb-10">
          <div>
            <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-amber-500 mb-2">{t("pricing.kicker")}</div>
            <h1 className="font-display text-5xl mb-3">{t("pricing.title")}</h1>
            <p className="text-text-secondary max-w-2xl">{t("pricing.subtitle")}</p>
          </div>
          <LanguageCurrencySelector />
        </div>

        {sub?.effective_status === "active" && (
          <div className="tray-inset p-4 mb-8 flex items-center justify-between flex-wrap gap-3" data-testid="current-plan-banner">
            <div className="flex items-center gap-3">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <div>
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-text-secondary">{t("pricing.currentPlan")}</div>
                <div className="font-display text-lg capitalize">{sub.plan} · {(sub.currency || "USD").toUpperCase()}</div>
              </div>
            </div>
            {sub.current_period_end && (
              <div className="text-right">
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-text-secondary">{t("pricing.renewsOn")}</div>
                <div className="font-mono-data text-sm">{new Date(sub.current_period_end).toLocaleDateString()}</div>
              </div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5">
          <Card
            id="trial"
            kicker={t("pricing.trialCard")}
            title="Free"
            desc={t("pricing.trialDesc")}
            mainPrice={formatPrice(0, currency)}
            period={`/ 3 ${t("pricing.daysUnit")}`}
            cta={status === "trialing" ? t("pricing.trialActive") : t("pricing.trialCta")}
            ctaIcon={Zap}
            onClick={startTrial}
            currentLabel={status === "trialing" ? `${t("pricing.trialActive")} ✓` : null}
          />

          <Card
            id="monthly"
            kicker={t("pricing.monthlyCard")}
            title={t("pricing.monthlyCard")}
            desc={t("pricing.monthlyDesc")}
            mainPrice={formatPrice(MONTHLY_USD, currency)}
            period={t("pricing.perMonth")}
            total={formatPrice(monthlyTotal, currency)}
            totalLabel={includesSetup ? t("pricing.totalToday") : t("pricing.billedMonthly")}
            cta={t("pricing.subscribeCta")}
            ctaIcon={Calendar}
            onClick={() => subscribe("monthly")}
            currentLabel={sub?.plan === "monthly" && sub.effective_status === "active" ? `${t("pricing.currentPlan")} ✓` : null}
          >
            {includesSetup && (
              <div className="tray-inset p-3 mb-4 text-center">
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-amber-500">{t("pricing.setupFee")}</div>
                <div className="font-display text-xl mt-0.5">{formatPrice(SETUP_USD, currency)}</div>
              </div>
            )}
          </Card>

          <Card
            id="annual"
            kicker={t("pricing.annualCard")}
            title={t("pricing.annualCard")}
            desc={t("pricing.annualDesc")}
            mainPrice={formatPrice(ANNUAL_USD, currency)}
            period={t("pricing.perYear")}
            total={formatPrice(annualTotal, currency)}
            totalLabel={includesSetup ? t("pricing.totalToday") : t("pricing.billedAnnually")}
            cta={t("pricing.subscribeCta")}
            ctaIcon={Sparkles}
            onClick={() => subscribe("annual")}
            featured
            badge={t("pricing.annualBadge")}
            currentLabel={sub?.plan === "annual" && sub.effective_status === "active" ? `${t("pricing.currentPlan")} ✓` : null}
          >
            {includesSetup && (
              <div className="tray-inset p-3 mb-4 text-center">
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-amber-500">{t("pricing.setupFee")}</div>
                <div className="font-display text-xl mt-0.5">{formatPrice(SETUP_USD, currency)}</div>
              </div>
            )}
          </Card>
        </div>

        {/* Features grid */}
        <div className="tray p-7 mt-10" data-testid="features-grid">
          <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-text-secondary mb-5">
            Everything included
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {features.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <span className="text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>

        {sub?.effective_status === "expired" && (
          <div className="mt-6 tray-inset p-4 flex items-center justify-center gap-3 text-amber-500" data-testid="expired-notice">
            <Lock className="w-4 h-4" />
            <span className="font-mono-data text-xs uppercase tracking-widest">{t("pricing.expired")}</span>
          </div>
        )}
      </div>
    </Layout>
  );
}
